import FileOpenIcon from '@mui/icons-material/FileOpen';
import { Chip } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GoogleMapBox } from '../../../../components/basic/GoogleMapBox';
import { GoogleSearchBox } from '../../../../components/basic/GoogleSearchBox';
import { FormCheck } from '../../../../components/form/FormCheck';
import { FormDatePicker } from '../../../../components/form/FormDatePicker';
import { FormInput } from '../../../../components/form/FormInput';
import { FormInputLabel } from '../../../../components/form/FormInputLabel';
import { FormRadio } from '../../../../components/form/FormRadio';
import { FormSelect } from '../../../../components/form/FormSelect';
import { FormTextEditor } from '../../../../components/form/FormTextEditor';
import { MultipleFileUploader } from '../../../../components/form/MultipleFileUploader';
import { projectFormValidations } from '../form';

export const BizInfo = (props) => {
  const {
    register,
    errors,
    control,
    setValue,
    subAreaMenus,
    methodologyMenus,
    methodologyEvidenceAndTerms,
    watch,
    googleAddress,
    setGoogleAddress,
  } = props;
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const validations = projectFormValidations(isEn);

  /* 타기관 연계 여부 확인 */
  const { isExported } = watch();
  const isExportedMenus = [
    {
      label: 'NO',
      value: false,
    },
    {
      label: 'OTHER_ORG_REQUIRED',
      value: true,
    },
  ];

  /* 방법론 약관 동의 여부 및 증빙서류 설정 */
  const termsList = methodologyEvidenceAndTerms?.terms;

  return (
    <article className='flex flex-col gap-4 w-[945px] py-8'>
      <header>
        <h2>{t('BIZ_INFO')}</h2>
      </header>
      <article className='flex flex-col gap-6'>
        <FormRadio
          label={t('OTHER_ORG')}
          error={errors.isExported}
          menus={isExportedMenus}
          control={control}
          defaultValue={isExported}
          required
          name='isExported'
        />
        {isExported === 'true' && (
          <div className='flex flex-col gap-4 p-8 border bg-slate-100 rounded-md'>
            <FormInput label={t('OTHER_ORG_NAME')} error={errors.exportedTitle} required sx={{ bgcolor: 'white' }} />
            <div className='grid grid-cols-2 gap-4'>
              <FormInput label={t('REGISTRAR')} error={errors.exportedOrgan} required sx={{ bgcolor: 'white' }} />
              <FormDatePicker
                label={t('REGISTRATION_DATE')}
                error={errors.exportedDate}
                required
                control={control}
                name='exportedDate'
                format='YYYY-MM-DD'
                sx={{ bgcolor: 'white' }}
              />
            </div>
          </div>
        )}
        <FormInput
          label={t('PROJECT')}
          required
          error={errors.title}
          {...register('title', {
            required: validations.title.required,
            validate: validations.title.validate,
          })}
        />
        <div className='flex flex-1 w-full gap-4'>
          <Controller
            control={control}
            name='area'
            rules={{
              required: validations.area.required,
            }}
            render={({ field }) => (
              <div className='flex w-1/4 flex-col gap-1'>
                <FormInputLabel label={t('AREA')} isRequired />
                <div className='flex gap-1 h-[56px] items-center'>
                  {['Removal', 'Reduction'].map((item) => (
                    <Chip
                      key={item}
                      label={t(item?.toUpperCase())}
                      color={field.value === item ? 'primary' : 'default'}
                      variant={field.value === item ? 'filled' : 'outlined'}
                      sx={{
                        minWidth: '100px',
                        span: {
                          color: field.value === item ? 'white' : 'primary.main',
                        },
                      }}
                      onClick={() => {
                        field.onChange(item);
                        setValue('subArea', '');
                      }}
                    />
                  ))}
                </div>
                {errors.area && <span className='text-xs text-red-500 mt-1'>{errors.area.message}</span>}
              </div>
            )}
          />
          <div className='flex-1'>
            <FormSelect
              label={t('SUB_AREA')}
              error={errors.subArea}
              menus={subAreaMenus}
              disabled={!watch('area')}
              required
              control={control}
              name='subArea'
              customChange={() => {
                setValue('methodologySeq', '');
                setValue('isTermsAgree', false);
                setValue('evidence', []);
              }}
              rules={{
                required: validations.subArea.required,
              }}
            />
          </div>
        </div>
        <FormSelect
          label={t('METHODOLOGY')}
          error={errors.methodologySeq}
          menus={methodologyMenus}
          disabled={!watch('area') || methodologyMenus?.length === 0}
          required
          control={control}
          name='methodologySeq'
          customChange={() => {
            setValue('isTermsAgree', false);
            setValue('evidence', []);
          }}
          rules={{
            required: validations.methodologySeq.required,
          }}
        />
        {/* 방법론 약관 동의 여부 */}
        {methodologyEvidenceAndTerms && termsList?.length > 0 && (
          <section className='bg-slate-50 p-4 rounded-md flex flex-col gap-8'>
            {termsList?.length > 0 && (
              <article>
                <header>
                  <h3 className='text-lg font-medium'>{t('METHODOLOGY') + ' ' + t('AGREE_TO_TERM')}</h3>
                </header>
                <div className='terms--list'>
                  <header>
                    <FormCheck
                      control={control}
                      name='isTermsAgree'
                      menus={[{ label: t('REVIEWED_AND_AGREE'), value: true }]}
                      groupProps={{
                        direction: 'row',
                      }}
                      labelProps={{
                        required: true,
                      }}
                      error={errors.isTermsAgree}
                    />
                  </header>
                  <ul className='grid grid-cols-3 gap-2 mt-4'>
                    {termsList.map((term) => (
                      <li
                        key={term.seq}
                        className='flex items-center justify-between cursor-pointer gap-2 border border-solid bg-white p-2 rounded-md border-gray-200 pb-2'
                      >
                        <span className='text-sm'>{term.terms.title}</span>
                        <FileOpenIcon
                          className='text-sm'
                          onClick={() => {
                            window.open(term.terms.termsFile, '_blank');
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            )}
          </section>
        )}
        <div className='grid grid-cols-2 w-full gap-4'>
          <FormDatePicker
            label={t('REDUCTION_START_DATE')}
            error={errors.reductionStartDate}
            required
            control={control}
            name='reductionStartDate'
            format='YYYY-MM-DD'
            rules={{
              required: validations.reductionStartDate.required,
            }}
          />
          <FormDatePicker
            label={t('REDUCTION_END_DATE')}
            error={errors.reductionEndDate}
            required
            control={control}
            minDate={watch('reductionStartDate')}
            name='reductionEndDate'
            format='YYYY-MM-DD'
            rules={{
              required: validations.reductionEndDate.required,
            }}
          />
        </div>
        <FormTextEditor
          label={t('PURPOSE')}
          error={errors.purpose}
          required
          rules={{ required: validations.purpose.required }}
          control={control}
          name='purpose'
        />
        <FormTextEditor
          label={t('CONTENT')}
          error={errors.content}
          required
          rules={{ required: validations.content.required }}
          control={control}
          name='content'
        />

        <div className='grid grid-cols-2 gap-4 items-center'>
          <div className='flex flex-col gap-4'>
            <FormInputLabel label={t('ADDRESS')} isRequired />
            <GoogleSearchBox value={googleAddress} onChange={(value) => setGoogleAddress(value)} />
            <FormInput
              error={errors.location?.addr2}
              control={control}
              name='location.addr2'
              placeholder={t('ADDRESS_DETAIL')}
            />
          </div>
          <Controller
            control={control}
            name='location.addr1'
            rules={validations['location.addr1']}
            error={errors.location?.addr1}
            render={({ field }) => (
              <GoogleMapBox
                initialAddress={googleAddress}
                size={{ width: '100%', height: '300px' }}
                onChange={(address) => {
                  field.onChange(address);
                }}
              />
            )}
          />
        </div>
        <MultipleFileUploader
          control={control}
          name='workplaceImages'
          label={t('BIZ_SITE_PHOTO')}
          path='project/workplace-images'
          fileType='image'
          max={10}
          error={errors.workplaceImages}
          rules={{
            required: isEn ? 'Please upload the workplace images.' : '사업장 이미지를 업로드해주세요.',
          }}
          required
        />
      </article>
    </article>
  );
};
